// @archigraph eHS4iX7G
// @archigraph 0OUno19C
// Login page for archigraph.io

import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ErrorMessage } from '../components/ui/ErrorMessage';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_FAILED_ATTEMPTS = 3;
const RATE_LIMIT_MS = 5 * 60 * 1000;

function openApp() {
  const tokens = useAuthStore.getState().tokens;
  const params = new URLSearchParams({
    accessToken: tokens?.accessToken || '',
    idToken: tokens?.idToken || '',
    refreshToken: tokens?.refreshToken || '',
  });
  window.location.href = `archigraph://auth/callback?${params.toString()}`;
}

export function LoginPage() {
  const { login, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const [showAppPrompt, setShowAppPrompt] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  useEffect(() => {
    if (!rateLimitedUntil) return;
    const remaining = rateLimitedUntil - Date.now();
    if (remaining <= 0) {
      setRateLimitedUntil(null);
      setRateLimitMsg('');
      setFailedAttempts(0);
      return;
    }
    setRateLimitMsg('Too many attempts. Try again in 5 minutes.');
    timerRef.current = setTimeout(() => {
      setRateLimitedUntil(null);
      setRateLimitMsg('');
      setFailedAttempts(0);
    }, remaining);
  }, [rateLimitedUntil]);

  const validateEmail = (v: string) => {
    if (!v.trim()) return 'Email is required';
    if (!EMAIL_REGEX.test(v)) return 'Please enter a valid email';
    return '';
  };

  const validatePassword = (v: string) => {
    if (!v) return 'Password is required';
    return '';
  };

  // @archigraph wPjLTEK9
  // @archigraph triggers|ui.website.login|interface.auth.login|api
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (rateLimitedUntil) return;

    const eErr = validateEmail(email);
    const pErr = validatePassword(password);
    setEmailError(eErr);
    setPasswordError(pErr);
    if (eErr || pErr) return;

    clearError();
    try {
      await login(email, password, rememberMe);
      openApp();
      setTimeout(() => setShowAppPrompt(true), 2000);
      return;
    } catch {
      const next = failedAttempts + 1;
      setFailedAttempts(next);
      if (next >= MAX_FAILED_ATTEMPTS) {
        setRateLimitedUntil(Date.now() + RATE_LIMIT_MS);
      }
    }
  };

  const isDisabled = isLoading || !!rateLimitedUntil;
  const displayError = rateLimitMsg || error;

  if (showAppPrompt) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <Container className="max-w-[400px]">
          <p className="text-xs uppercase tracking-widest text-primary mb-4 font-bold">
            // authenticated
          </p>
          <h1 className="text-2xl font-bold text-text mb-2">Opening ArchiGraph...</h1>
          <p className="text-xs text-text-lighter mb-8">
            Didn't open? Make sure ArchiGraph is installed, then try again.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" size="md" onClick={openApp}>
              Open ArchiGraph
            </Button>
            <Link
              to="/download"
              className="inline-flex items-center justify-center rounded-none font-medium uppercase tracking-wider border border-border text-text-light hover:text-text hover:border-text-lighter px-6 py-2 text-xs transition-all"
            >
              Download
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12">
      <Container className="max-w-[400px]">
        <p className="text-xs uppercase tracking-widest text-primary mb-4 font-bold">
          // authenticate
        </p>
        <h1 className="text-2xl font-bold text-text mb-1">Welcome back</h1>
        <p className="text-xs text-text-lighter mb-8">Log in to your ArchiGraph account</p>

        {displayError && (
          <ErrorMessage
            message={displayError}
            onDismiss={rateLimitMsg ? undefined : clearError}
            className="mb-6"
          />
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(''); clearError(); }}
            onBlur={() => setEmailError(validateEmail(email))}
            error={emailError}
            disabled={isDisabled}
          />

          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setPasswordError(''); clearError(); }}
            onBlur={() => setPasswordError(validatePassword(password))}
            error={passwordError}
            disabled={isDisabled}
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded-none border-border bg-bg text-primary focus:ring-primary cursor-pointer accent-primary"
              />
              <label htmlFor="remember-me" className="text-xs text-text-light cursor-pointer select-none">
                Remember me
              </label>
            </div>
            <Link to="/forgot-password" className="text-xs text-primary hover:text-primary-light font-medium">
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isLoading}
            disabled={isDisabled}
            className="w-full"
          >
            Log In
          </Button>
        </form>

        <div className="mt-8 pt-6 border-t border-border">
          <p className="text-xs text-text-lighter">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary hover:text-primary-light font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </Container>
    </div>
  );
}
